// src/components/character/PrintableCharacterSheet.jsx
import React, { useMemo } from "react";

function mapById(list, pickId) {
    const m = new Map();
    (list || []).forEach((x) => {
        const id =
            pickId?.(x) ??
            x?.raceId ??
            x?.skillsetId ??
            x?.skillId ??
            x?.id;
        if (id != null) m.set(id, x);
    });
    return m;
}


const isZeroValue = (value) =>
    value === 0 || (typeof value === "string" && value.trim() === "0");

// Attribute/armor points come back from the API as numeric strings (e.g.
// "0.00"), which isZeroValue's exact "0" string match doesn't catch — this
// coerces through Number() instead, the same way CharacterForm's hideZero
// does for these same fields.
const isZeroPoints = (value) => !Number(value);

// subValue renders as a smaller, non-bold annotation after the main value —
// e.g. "1062 (838)" for Skill Points (Spent), the same treatment as the SP
// cost annotation next to a skill's ranks in the skill tables below.
// muted lightens the label + value text (secondary/less-important fields).
function Field({ label, value, subValue, muted = false }) {
    const isZero = isZeroValue(value);
    return (
        <div className={`characterform-group${muted ? " characterform-group--muted" : ""}`}>
            <div className="characterform-label">{label}</div>
            <div className={`characterform-value${isZero ? " is-zero" : ""}`}>
                {value ?? ""}
                {subValue != null && subValue !== "" && (
                    <span className="characterform-value-sub"> {subValue}</span>
                )}
            </div>
        </div>
    );
}


export default function PrintableCharacterSheet({
                                                    character,
                                                    races,
                                                    skillsets,
                                                    availableSkills,
                                                    qrDataUrl,
                                                }) {
    const raceById = useMemo(() => mapById(races, (r) => r.raceId), [races]);
    const skillsetById = useMemo(
        () => mapById(skillsets, (s) => s.skillsetId),
        [skillsets]
    );
    const skillById = useMemo(
        () => mapById(availableSkills, (s) => s.skillId),
        [availableSkills]
    );

    const raceName =
        character?.raceId != null ? raceById.get(character.raceId)?.name || "" : "";
    const primaryName =
        character?.skillsetPrimary != null
            ? skillsetById.get(character.skillsetPrimary)?.name || ""
            : "";
    const secondaryName =
        character?.skillsetSecondary != null
            ? skillsetById.get(character.skillsetSecondary)?.name || ""
            : "";

    // ---- NEW: grouping exactly like SkillList ----
    const localSkillsetMap = useMemo(() => {
        const m = {};
        (skillsets || []).forEach((s) => (m[String(s.skillsetId)] = s.name));
        m.static = "Static";
        return m;
    }, [skillsets]);

    const labelForGroup = (groupKey) =>
        groupKey === "transform"
            ? "Transform Abilities"
            : localSkillsetMap?.[groupKey] || "Unknown";

    const grouped = useMemo(() => {
        const skillsArray = Array.isArray(character?.skills) ? character.skills : [];
        const out = skillsArray.reduce((acc, entry) => {
            const base = skillById.get(entry.skillId) || {};

            // Determine group key (match SkillList)
            let groupKey;
            if (base && base.primaryBaseCost === 0) groupKey = "static";
            else if (base && base.skillset !== undefined) groupKey = String(base.skillset);
            else if (base && base.source === "transform") groupKey = "transform";
            else groupKey = "unknown";

            (acc[groupKey] ||= []).push({ ...base, ...entry });
            return acc;
        }, {});

        // Sort groups: 'static' last, otherwise by label (case-insensitive)
        const keys = Object.keys(out).sort((a, b) => {
            // Static always first
            if (a === "static" && b !== "static") return -1;
            if (b === "static" && a !== "static") return 1;

            // Transform always last
            if (a === "transform" && b !== "transform") return 1;
            if (b === "transform" && a !== "transform") return -1;
            const A = labelForGroup(a).toUpperCase();
            const B = labelForGroup(b).toUpperCase();
            return A.localeCompare(B);
        });

        // Sort skills within each group by name
        return keys.map((k) => ({
            key: k,
            label: labelForGroup(k),
            items: out[k].slice().sort((s1, s2) => (s1.name || "").localeCompare(s2.name || "")),
        }));
    }, [character?.skills, skillById, localSkillsetMap]);

    const getSkillRate = (skill) => {
        const base = Number(skill?.baseCostUsed ?? 0);
        const inc  = Number(skill?.incrementCostUsed ?? 0);
        if (base === 0) return "-";
        if (inc === 0)  return String(base);
        return `${base}+${inc}`;
    }

    const getSPCostString = (skill) => {
        if (skill.cost > 0) {
            if (skill.incrementCostUsed > 0) {
                return `${skill.cost} SP (${getSkillRate(skill)})`;
            }
            return `${skill.cost} SP`;
        }
        return "-";
    }

    const allSkills = character.skills?.map(s => skillById.get(s.skillId)) || [];

    // Only include skills that actually have a non-empty description
    const refSkills = useMemo(() => {
        return (allSkills || [])
            .filter(Boolean)
            .filter(s => {
                const desc = typeof s?.description === "string" ? formatRefText(s.description).trim() : "";
                return desc.length > 0;
            });
    }, [allSkills]);


    // Convert simple HTML breaks/paragraphs to newlines; strip other tags.
    function formatRefText(raw) {
        let t = raw || "";
        t = t.replace(/<br\s*\/?>/gi, "\n");
        t = t.replace(/<\/p>\s*<p>/gi, "\n");
        t = t.replace(/<\/?p\s*>/gi, "");     // remove remaining <p> wrappers
        t = t.replace(/<\/?[^>]+>/g, "");     // strip any other tags
        return t;
    }

    return (
        <div className="pcs-page theme-print">
            {/* Everything up to .pcs-break prints on page 1. This wrapper is
                sized to fill the printed page so pcs-page1-footer (Rests +
                Armor Rep Values) can be pinned to the bottom via margin-top:
                auto, instead of just trailing wherever the skills list ends. */}
            <div className="pcs-page1">
            {/* The title and the QR code are siblings in this row (rather than
                the QR being anchored to the character-form row below) so the
                QR code's top edge lines up with the title's top edge. */}
            <div className="pcs-top-layout">
                <div className="pcs-top-left">
                    {/* Page header (single line) */}
                    <div className="pcs-page-header">
                        <div className="pcs-head-top">
                            <span className="pcs-head-title">Chronicles Character Sheet</span>
                            <span className="pcs-head-version">v7.0.0</span>
                        </div>
                    </div>

                    <section className="pcs-section">
                        <div className="characterform-form">
                            <div className="characterform-desktop-top">
                                <div className="characterform-row characterform-row-inline">
                                    <Field label="Name" value={character?.name} />
                                    <Field label="Player" value={character?.playerName} />
                                    <Field label="Race" value={raceName} />
                                    <Field label="Sub Race" value={character?.subRace} muted />
                                </div>

                                <div className="characterform-row characterform-row-inline">
                                    <Field label="Primary" value={primaryName} />
                                    <Field label="Secondary" value={secondaryName} />
                                    <Field label="Transform" value={character?.transformName || ""} muted />
                                </div>

                                <div className="characterform-row characterform-row-inline">
                                    <Field
                                        label="Skill Points (Spent)"
                                        value={character?.skillPointsTotal}
                                        subValue={`(${character?.skillPointsSpent ?? 0})`}
                                    />
                                    <Field label="SP Debt" value={character?.skillPointsDebt} muted />
                                    <Field label="Effective SP" value={character?.skillPointsEffective} muted />
                                    <Field label="Unspent SP" value={character?.skillPointsUnspent} muted />
                                    <Field label="Goblin Points" value={0} muted />
                                    <Field label="Bank" value={character?.bankBalance ?? 0} muted />
                                </div>
                            </div>

                            {/* Attribute points share a line with Body/Armor Cap/Dex Armor, in a
                                fixed order. Zero-valued attributes (and Dex Armor) are omitted
                                entirely rather than shown as a blank/"0" box. Body and Armor Cap
                                always show. */}
                            <div className="characterform-row characterform-row-inline characterform-row-pool">
                                <Field label="Body" value={character?.bodyPoints} />
                                <Field label="Armor Cap" value={character?.maxArmor} />
                                {!isZeroPoints(character?.dexArmor) && (
                                    <Field label="Dex Armor" value={character?.dexArmor} />
                                )}
                                {!isZeroPoints(character?.thaumaturgyPoints) && (
                                    <Field label="Thaum." value={character?.thaumaturgyPoints} />
                                )}
                                {!isZeroPoints(character?.arcanePoints) && (
                                    <Field label="Arcane" value={character?.arcanePoints} />
                                )}
                                {!isZeroPoints(character?.naturePoints) && (
                                    <Field label="Nature" value={character?.naturePoints} />
                                )}
                                {!isZeroPoints(character?.spiritPoints) && (
                                    <Field label="Spirit" value={character?.spiritPoints} />
                                )}
                                {!isZeroPoints(character?.agilityPoints) && (
                                    <Field label="Agility" value={character?.agilityPoints} />
                                )}
                                {!isZeroPoints(character?.staminaPoints) && (
                                    <Field label="Stamina" value={character?.staminaPoints} />
                                )}
                            </div>
                        </div>
                    </section>
                </div>

                <div className="pcs-side-column">
                    {qrDataUrl && (
                        <div className="pcs-head-qr">
                            <img src={qrDataUrl} alt="Character build QR code" className="pcs-qr-img" />
                        </div>
                    )}

                    <div className="pcs-head-fillers">
                        <span className="pcs-head-blank is-date">Event Date</span>
                        <span className="pcs-head-blank is-fee">Fee/Meal</span>
                        <span className="pcs-head-blank is-approval">Approval</span>
                    </div>
                </div>
            </div>

            {/* Skills */}
            <section className="pcs-section">
                {/*<h2 className="pcs-h2">Skills</h2>*/}

                {grouped.length === 0 && <div className="pcs-empty">No skills on this character.</div>}

                {/* NEW: two-column wrapper */}
                <div className="pcs-skill-columns">
                    <div className="pcs-group" key="Knowledges">
                        <div className="pcs-group-title">Knowledges</div>
                        <table className="pcs-table">
                            <thead>
                            <tr>
                                <th style={{ width: "30%" }}>Ranks</th>
                                <th style={{ width: "70%" }}>Skill</th>
                            </tr>
                            </thead>
                            <tbody>
                            {character.knowledges.map((k) => {

                                return (
                                    <tr key={k.knowledgeId}>
                                        <td className="pcs-name pcs-right">
                                            <span className="pcs-ranks">{k.ranks}</span>
                                        </td>
                                        <td className="pcs-name">{k.name}</td>
                                        <td className="pcs-notes"></td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                    {grouped.map(({ key, label, items }) => (
                        <div className="pcs-group" key={key}>
                            <div className="pcs-group-title">{label}</div>
                            <table className="pcs-table">
                                <thead>
                                <tr>
                                    <th style={{ width: "30%" }}>Ranks</th>
                                    <th style={{ width: "70%" }}>Skill</th>
                                </tr>
                                </thead>
                                <tbody>
                                {items.map((r) => {
                                    let notes = r.description || r.shortDescription || "";
                                    if (typeof notes === "string") {
                                        const nl = notes.indexOf("\n");
                                        if (nl > -1) notes = notes.slice(0, nl);
                                    }
                                    return (
                                        <tr key={r.skillId || r.name}>
                                            <td className="pcs-name pcs-right">
                                                <span className="pcs-sp">{getSPCostString(r)}</span>
                                                <span className="pcs-ranks">
                                                    {r.incrementCostUsed > 0 ? (r.ranks ?? "") : ""}
                                                </span>
                                            </td>

                                            <td className="pcs-name">{r.name || `#${r.skillId}`}</td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </section>
            <div className="pcs-page1-footer">
                {/* Rests sign-off (plot member initials each rest) */}
                <section className="pcs-section pcs-rests">
                    <div className="pcs-rests-line">
                        <span className="pcs-rests-label">Rests:</span>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <span className="pcs-head-blank pcs-rests-blank" key={i}>
                                Time and Plot Signature
                            </span>
                        ))}
                    </div>
                </section>

                {/* Armor Rep footer */}
                <section className="pcs-section pcs-armor-rep">
                    <div className="pcs-armor-line">
                        <span className="pcs-armor-label">Armor Rep Values:</span>
                        <span className="pcs-armor-part">Torso <span className="pcs-armor-values">0 | 10 | 20 | 40</span></span>
                        <span className="pcs-armor-part">Arm <span className="pcs-armor-values">0 | 5 | 10 | 20</span></span>
                        <span className="pcs-armor-part">Leg <span className="pcs-armor-values">0 | 5 | 10 | 20</span></span>
                        <span className="pcs-head-blank pcs-armor-initials">Total</span>
                        <span className="pcs-head-blank pcs-armor-initials">Initials</span>
                    </div>
                </section>
            </div>
            </div>
            {/* ---------- Skill Reference (new page) ---------- */}
            <div className="pcs-break" />
            <section className="pcs-section pcs-skill-ref">
                <h2 className="pcs-h2">Skill Reference</h2>
                <div className="pcs-ref-group">
                    {refSkills.map((s) => (
                        <div className="pcs-ref-item" key={`ref-static-${s.skillId || s.name}`}>
                            <div className="pcs-ref-name">{s.name}</div>
                            {/* Show description as its own block so we can control line-height */}
                            <div className="pcs-ref-desc">
                                {formatRefText(s.description)}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
